// src/components/assistant/ThreadTitle.tsx
//
// Renombrar conversaciones en línea: en la lista (doble clic o botón «Renombrar»)
// y en el encabezado del chat. Enter guarda, Esc cancela, salir del campo guarda.
// El nombre manual queda en el backend como `title_source=user` y el título
// automático ya no lo pisa.

import { useEffect, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';

export const TITLE_MAX = 80;

export function RenameInput({
  initial,
  onSave,
  onCancel,
  className = '',
  ariaLabel = 'Nuevo nombre de la conversación',
}: {
  initial: string;
  onSave: (title: string) => void;
  onCancel: () => void;
  className?: string;
  ariaLabel?: string;
}) {
  const [val, setVal] = useState(initial);
  const ref = useRef<HTMLInputElement | null>(null);
  const done = useRef(false);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  const finish = (save: boolean) => {
    if (done.current) return;
    done.current = true;
    const t = val.replace(/\s+/g, ' ').trim();
    if (save && t && t !== initial.trim()) onSave(t.slice(0, TITLE_MAX));
    else onCancel();
  };

  return (
    <input
      ref={ref}
      value={val}
      maxLength={TITLE_MAX}
      aria-label={ariaLabel}
      onChange={(e) => setVal(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          finish(true);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          finish(false);
        }
      }}
      onBlur={() => finish(true)}
      onClick={(e) => e.stopPropagation()}
      className={`w-full min-w-0 rounded-md border border-ring bg-background px-2 py-1 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring ${className}`}
    />
  );
}

/** Título del encabezado del chat con botón para renombrar. */
export function ThreadHeaderTitle({
  title,
  fallback,
  onRename,
  as: Tag = 'span',
  className = '',
}: {
  title: string | null;
  fallback: string;
  onRename?: (title: string) => Promise<void> | void;
  as?: 'span' | 'h1' | 'h2';
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const shown = title || fallback;
  if (editing && onRename) {
    return (
      <div className="min-w-0 flex-1">
        <RenameInput
          initial={title || ''}
          onSave={(t) => {
            setEditing(false);
            void onRename(t);
          }}
          onCancel={() => setEditing(false)}
        />
      </div>
    );
  }
  return (
    <div className="group flex min-w-0 flex-1 items-center gap-1">
      <Tag
        className={`truncate ${className}`}
        title={shown}
        onDoubleClick={onRename ? () => setEditing(true) : undefined}
      >
        {shown}
      </Tag>
      {onRename && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label="Renombrar conversación"
          title="Renombrar"
          className="shrink-0 rounded-md p-1 text-muted-foreground opacity-100 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 md:opacity-0 md:group-hover:opacity-100"
        >
          <Pencil size={13} aria-hidden />
        </button>
      )}
    </div>
  );
}

/** Ítem de la lista de conversaciones: clic abre, doble clic o lápiz renombra. */
export function ThreadListRow({
  title,
  active,
  onSelect,
  onRename,
  subtitle,
  icon,
  className = '',
}: {
  title: string;
  active: boolean;
  onSelect: () => void;
  onRename?: (title: string) => Promise<void> | void;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  if (editing && onRename) {
    return (
      <div className={`px-1 py-1 ${className}`}>
        <RenameInput
          initial={title}
          onSave={(t) => {
            setEditing(false);
            void onRename(t);
          }}
          onCancel={() => setEditing(false)}
        />
      </div>
    );
  }
  return (
    <div className={`group relative flex items-center rounded-lg ${active ? 'bg-muted' : 'hover:bg-muted/60'} ${className}`}>
      <button
        type="button"
        onClick={onSelect}
        onDoubleClick={onRename ? () => setEditing(true) : undefined}
        aria-current={active ? 'true' : undefined}
        title={title}
        className={`flex min-w-0 flex-1 items-start gap-2 rounded-lg py-2 pl-3 text-left text-sm ${
          onRename ? 'pr-9 md:pr-3 md:group-focus-within:pr-9 md:group-hover:pr-9' : 'pr-3'
        } ${
          active ? 'font-medium text-foreground' : 'text-foreground/75'
        }`}
      >
        {icon}
        <span className="min-w-0 flex-1">
          <span className="block truncate">{title}</span>
          {subtitle}
        </span>
      </button>
      {onRename && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label={`Renombrar «${title}»`}
          title="Renombrar"
          className="absolute right-1 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground opacity-100 transition-opacity hover:bg-background hover:text-foreground focus-visible:opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100"
        >
          <Pencil size={13} aria-hidden />
        </button>
      )}
    </div>
  );
}
